import {PageHeader} from '../ui/PageHeader';
import {ResponsiveTable} from '../ui/ResponsiveTable';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api } from '../api';
import { DateOnlyInput } from '../components/DateOnlyInput';
import { formatDateOnly, formatDateTime } from '../dates';
import { Loading, ErrorState } from '../ui/State';
import './engine-workspaces.css';
type Card = {
  id: string;
  buildingId: string;
  building: string;
  title: string;
  version: number;
  health: string;
  timing: string;
  href: string;
  canCoordinate: boolean;
  contributions: {
    id: string;
    brief: string;
    engine: string;
    targetOn: string | null;
    ownership: string;
    response: string;
    source: { coverage: string; href?: string; state?: string };
    proof: { accepted: boolean; current: boolean; satisfied: boolean } | undefined;
  }[];
  commitments: { id: string; promise: string; dueOn: string | null }[];
  escalations: { action_id: string; href: string; status: string; owner: string; due_on: string | null; severity: string; exception_status: string }[];
  escalationCoverage: string;
};
type Oversight = {
  buildings:{id:string;name:string}[];
  lens: string;
  scopeNotice: string;
  emptyReason: string | null;
  calculatedAt: string;
  canViewAuthorised: boolean;
  canViewOperations: boolean;
  outcomes: Card[];
  hasMore: boolean;
  coverage: string;
  work: {
    items: {
      key: string;
      title: string;
      building: string;
      status: string;
      priority: string | null;
      owner: string | null;
      dueOn: string | null;
      href: string;
      nextAction: string;
      outcomes: { title: string; href: string }[];
      exceptions: { id: string; severity: string; status: string }[];
    }[];
    total: number;
    complete: boolean;
    coverage: { source: string; status: string; reason: string | null }[];
    capacity: {
      status: string;
      reason: string;
      people: {
        id: string;
        name: string;
        committedMinutes: number;
        unknownCommitmentCount: number;
        forecastMinutes: number;
        unknownDurationCount: number;
        actualMinutes: number;
      }[];
    };
  };
  visits: {
    id: string;
    buildingId:string;
    href: string;
    state: string;
    pendingChange: boolean;
    coverage: string;
    recordedActualMinutes: number;
    unknownActualCount: number;
    members: {
      startsAt:string;endsAt:string;
      reportId: string;
      reportHref: string;
      status: string;
      findings: { coverage: string; followUps?: { kind: string; status: string; href: string }[]; continuations?: { status: string; href: string | null }[] };
    }[];
  }[];
  moreVisits: boolean;
  visitCoverage: string;
};
export function OutcomeOversight() {
  const { code } = useParams(),
    [params, setParams] = useSearchParams(),
    building = params.get('building') ?? undefined,
    lens = code ? 'Engine' : params.get('lens') === 'Authorised' ? 'Authorised' : params.get('lens') === 'Operations' ? 'Operations' : 'Mine',
    offset = Number(params.get('offset') ?? 0),
    sourceOffset = Number(params.get('sourceOffset') ?? 0),
    visitOffset = Number(params.get('visitOffset') ?? 0);
  const [data, setData] = useState<Oversight>(),
    [error, setError] = useState<unknown>(),
    [refresh, setRefresh] = useState(0),
    [busy, setBusy] = useState(false),
    [target, setTarget] = useState<{ card: Card; contributionId: string | null; commitmentId: string | null }>(),
    [recipients, setRecipients] = useState<{ id: string; name: string }[]>([]),
    [recipient, setRecipient] = useState(''),
    [condition, setCondition] = useState('Handoff'),
    [severity, setSeverity] = useState('Medium'),
    [due, setDue] = useState(''),
    [basis, setBasis] = useState(''),
    [saved, setSaved] = useState<string>();
  const recipientGeneration = useRef(0);
  const [recipientCoverage,setRecipientCoverage] = useState('Loading');
  useEffect(() => {
    recipientGeneration.current++;
    setRecipients([]);
    const c = new AbortController();
    setData(undefined);
    setError(undefined);
    setTarget(undefined);
    const query = new URLSearchParams({ lens, offset: String(offset), sourceOffset: String(sourceOffset), visitOffset: String(visitOffset) });
    if (code) query.set('engine', code);
    if (building) query.set('building', building);
    void api<Oversight>('/api/outcome-oversight?' + query, { signal: c.signal })
      .then((r) => {
        if (!c.signal.aborted) setData(r);
      })
      .catch((e) => {
        if (!c.signal.aborted) setError(e);
      });
    return () => c.abort();
  }, [lens, code, building, offset, sourceOffset, visitOffset, refresh]);
  function page(key: string, value: number) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, String(value));
    else next.delete(key);
    setParams(next);
  }
  async function choose(card: Card, contributionId: string | null, commitmentId: string | null = null) {
    const generation=++recipientGeneration.current;
    setRecipientCoverage('Loading');
    setTarget({ card, contributionId, commitmentId });
    setRecipients([]);
    setRecipient('');
    setCondition(commitmentId ? 'Commitment' : !card.contributions.find((c) => c.id === contributionId)?.proof?.accepted ? 'Handoff' : 'Evidence');
    setBasis('');
    setDue('');
    setSeverity('Medium');
    setError(undefined);
    try {
      const r = await api<{ items: { id: string; name: string }[];coverage:string }>('/api/outcome-escalation-recipients?building=' + card.buildingId);
      if(generation!==recipientGeneration.current)return;
      setRecipients(r.items);setRecipientCoverage(r.coverage);
    } catch (e) {
      if(generation===recipientGeneration.current)setError(e);
    }
  }
  const requestIds = useRef(new Map<string, string>());
  async function escalate() {
    if (!target || busy) return;
    setBusy(true);
    setError(undefined);
    const payload = {
      outcomeId: target.card.id,
      buildingId: target.card.buildingId,
      contributionId: target.contributionId,
      commitmentId: target.commitmentId,
      condition,
      expectedVersion: target.card.version,
      recipientId: recipient,
      severity,
      dueOn: due || null,
      basis
    };
    const signature = JSON.stringify(payload);
    let requestId = requestIds.current.get(signature);
    if (!requestId) {
      requestId = crypto.randomUUID();
      requestIds.current.set(signature, requestId);
    }
    try {
      const r = await api<{ actionId: string; reused: boolean }>('/api/outcome-escalations', { method: 'POST', body: JSON.stringify({ ...payload, idempotencyKey: requestId }) });
      setSaved(r.reused ? 'Existing coordination Action reused' : 'Coordination Action assigned');
      setTarget(undefined);
      setRefresh((v) => v + 1);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="engine-workspaces oversight-workspace">
      <PageHeader title={code ? 'Engine contributions' : 'Portfolio oversight'} context="Building results, original work and operational follow-up in one view."
       secondaryActions={[
        {kind:'button',label:'Refresh oversight',onClick:()=>setRefresh(v=>v+1)},{kind:'link',label:'My assigned work',to:'/work'},
        ...(code?[{kind:'link' as const,label:'Engine workspace',to:'/engines/'+code+(building?'?building='+building:'')}]:[])]}/>
      {!code && (
        <label>
          Oversight scope{' '}
          <select
            value={lens}
            onChange={(e) => {
              const next = new URLSearchParams();
              if (building) next.set('building', building);
              if (e.target.value !== 'Mine') next.set('lens', e.target.value);
              setParams(next);
            }}
          >
            <option value="Mine">My appointed and acting buildings</option>
            {data?.canViewAuthorised && <option value="Authorised">All authorised buildings</option>}
            {data?.canViewOperations && <option value="Operations">Operations across authorised buildings</option>}
          </select>
        </label>
      )}
      {saved && <p role="status">{saved}</p>}
      {error !== undefined && <ErrorState error={error} />} {!data && error === undefined && <Loading />}
      {data && (
        <>
          <p>
            {data.scopeNotice} · Refreshed {formatDateTime(data.calculatedAt)}
          </p>
          {data.emptyReason && <p role="status">{data.emptyReason}</p>}
          <section aria-label="Building results">
            <h2>Building results and commitments</h2>
            <p>Outcome coverage: {data.coverage}. Source coverage and acceptance are checked individually.</p>
            {data.outcomes.map((card) => (
              <article key={card.id}>
                <h3>
                  <Link to={card.href}>{card.title}</Link> · {card.building}
                </h3>
                <p>
                  {card.health} · Commitment dates: {card.timing}
                </p>
                {card.contributions.map((c) => (
                  <div key={c.id}>
                    <h4>{c.brief}</h4>
                    <p>
                      {c.engine} · {c.ownership} · {c.response} · {c.targetOn ? formatDateOnly(c.targetOn) : 'Date unconfirmed'} · {c.source.coverage}
                    </p>
                    <p>{c.proof?.current && c.proof.satisfied ? 'Current source criterion satisfied' : 'Original evidence or responsibility needs review'}</p>
                    {c.source.href && <Link to={c.source.href}>Open original work</Link>}
                    {card.canCoordinate && (!c.proof?.accepted || !c.proof.current || !c.proof.satisfied) && (
                      <button onClick={() => void choose(card, c.id)}>Escalate {c.brief}</button>
                    )}
                  </div>
                ))}
                <ul>
                  {card.commitments.map((c) => (
                    <li key={c.id}>
                      {c.promise} · {c.dueOn ? formatDateOnly(c.dueOn) : 'Promise date unconfirmed'}
                      {card.canCoordinate && <button onClick={() => void choose(card, null, c.id)}>Review commitment escalation</button>}
                    </li>
                  ))}
                </ul>
                <p>Escalation coverage: {card.escalationCoverage}</p>
                <ul>
                  {card.escalations.map((e) => (
                    <li key={e.action_id}>
                      <Link to={e.href}>Coordination Action</Link> · {e.status} · {e.owner} · {e.severity} · {e.due_on ? formatDateOnly(e.due_on) : 'Date unconfirmed'} ·
                      Exception {e.exception_status}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
            {offset > 0 && <button onClick={() => page('offset', Math.max(0, offset - 20))}>Previous outcomes</button>}
            {data.hasMore && <button onClick={() => page('offset', offset + 20)}>More outcomes</button>}
          </section>
          {target && (
            <section aria-label="Assign coordination escalation">
              <h2>Assign coordination escalation</h2>
            {recipientCoverage==='Partial'&&<p>Recipient list is partial. Only authorised people in the loaded identity candidates are shown.</p>}
              <p>
                {target.card.title} · {target.contributionId ? target.card.contributions.find((c) => c.id === target.contributionId)?.brief : 'Stakeholder commitment'}
              </p>
              <label>
                Unresolved condition{' '}
                <select value={condition} onChange={(e) => setCondition(e.target.value)}>
                  {(target.commitmentId ? ['Commitment'] : ['Handoff', 'Ownership', 'Evidence', 'Dependency']).map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label>
                Responsible person{' '}
                <select value={recipient} onChange={(e) => setRecipient(e.target.value)}>
                  <option value="">Choose an authorised recipient</option>
                  {recipients.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Escalation severity{' '}
                <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                  {['Low', 'Medium', 'High', 'Critical'].map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
              </label>
              <label>
                Coordination due date <DateOnlyInput value={due} onChange={setDue} />
              </label>
              <label>
                Reason and safe impact <textarea value={basis} onChange={(e) => setBasis(e.target.value)} maxLength={500} />
              </label>
              <button disabled={busy || !recipient || basis.trim().length < 3} onClick={() => void escalate()}>
                Assign escalation
              </button>
              <button disabled={busy} onClick={() => setTarget(undefined)}>
                Cancel
              </button>
            </section>
          )}
          <section aria-label="Original operational work">
            <h2>Original operational work</h2>
            {code&&<p>The engine filter applies to contributions above. Original work and capacity cover the authorised buildings in this view.</p>}
            <p>
              Loaded original sources: {data.work.total}. {data.work.complete ? 'Available source coverage' : 'Coverage incomplete; these are loaded sources only'}
            </p>
            <details className="oversight-source-coverage" open={!data.work.complete}><summary>Original source coverage</summary><ul>
              {data.work.coverage.map((c) => (
                <li key={c.source}>
                  {c.source}: {c.status}
                  {c.reason ? ' · ' + c.reason : ''}
                </li>
              ))}
            </ul></details>
            {data.work.items.map((item) => (
              <article key={item.key}>
                <h3>
                  <Link to={item.href}>{item.title}</Link>
                </h3>
                <p>
                  {item.building} · {item.priority ?? 'Urgency unconfirmed'} · {item.status} · {item.owner ?? 'Next person not yet assigned'} ·{' '}
                  {item.dueOn ? formatDateOnly(item.dueOn) : 'Deadline unconfirmed'}
                </p>
                <p>{item.nextAction}</p>
                {item.outcomes.map((o) => (
                  <Link key={o.href} to={o.href}>
                    {o.title}
                  </Link>
                ))}
                {item.exceptions.map((e) => (
                  <p key={e.id}>
                    Exception {e.status} · {e.severity}
                  </p>
                ))}
              </article>
            ))}
            {sourceOffset > 0 && <button onClick={() => page('sourceOffset', Math.max(0, sourceOffset - 50))}>Previous source work</button>}
            {sourceOffset + data.work.items.length < data.work.total && <button onClick={() => page('sourceOffset', sourceOffset + 50)}>More source work</button>}
          </section>
          <section aria-label="Operations follow-up">
            <h2>Operations follow-up</h2>
            <p>Coverage: {data.visitCoverage}</p>
            {!data.visits.length&&<p>No operational follow-up entries were returned.</p>}
            {data.visits.map((v) => (
              <article key={v.id}>
                <h3>
                  <Link to={v.href}>Original attendance · {data.buildings.find(b=>b.id===v.buildingId)?.name??'Building context unavailable'}</Link>
                </h3>
                <p>
                  {v.state} · {v.pendingChange ? 'Reassignment or cover proposed' : 'No pending change'} · {v.coverage}
                </p>
                <p>
                  Recorded actual effort {v.recordedActualMinutes} minutes; {v.unknownActualCount} activities with unknown actual effort
                </p>
                {v.members.map((m) => (
                  <div key={m.reportId}>
                    <Link to={m.reportHref}>Original report</Link> · {m.status}
                    <p>{formatDateTime(m.startsAt)} to {formatDateTime(m.endsAt)}</p>
                    <p>Findings coverage: {m.findings.coverage}</p>
                    <ul>
                      {m.findings.followUps?.map((f, i) => (
                        <li key={i}>
                          <Link to={f.href}>{f.kind} follow-up</Link> · {f.status}
                        </li>
                      ))}
                      {m.findings.continuations?.map((c, i) => (
                        <li key={'c' + i}>
                          {c.href ? <Link to={c.href}>Original return report</Link> : 'Return awaiting booking'} · {c.status ?? 'Unassigned'}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </article>
            ))}
            {visitOffset > 0 && <button onClick={() => page('visitOffset', Math.max(0, visitOffset - 10))}>Previous attendance</button>}
            {data.moreVisits && <button onClick={() => page('visitOffset', visitOffset + 10)}>More attendance</button>}
          </section>
          <section aria-label="Source capacity">
            <h2>Source capacity</h2>
            <p>
              {data.work.capacity.status} · {data.work.capacity.reason}
            </p>

{!data.work.capacity.people.length&&<p>No people were returned by the capacity source.</p>}
<ResponsiveTable>
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Booked minutes</th>
                  <th>Forecast minutes</th>
                  <th>Recorded actual minutes</th>
                </tr>
              </thead>
              <tbody>
                {data.work.capacity.people.map((p) => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>
                      {p.committedMinutes}
                      {p.unknownCommitmentCount ? ' · Unknown reservations present' : ''}
                    </td>
                    <td>
                      {p.forecastMinutes}
                      {p.unknownDurationCount ? ' · Unknown durations present' : ''}
                    </td>
                    <td>{p.actualMinutes}</td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>

          </section>
        </>
      )}
    </div>
  );
}
